'use client';
import React, { useState, useEffect } from 'react';
import {
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha'; // ← still unused, keep if you’ll re‑enable it
import s from './ConsultationModal.module.scss';
import { emailjsSend } from '../emailjsConfig';
import Thankssvg from '../assets/Thankssvg.svg';

const ConsultationModal = ({ open, handleClose }) => {
  /**──────────────────
   * ‣ Form state
   * ──────────────────*/
  const [value, setValue] = useState('Residential');
  const [consult, setConsult] = useState({});

  /** Validation state */
  const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidPostal, setIsValidPostal] = useState(false);

  /** UI helpers */
  const [disabled, setDisabled] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  /**──────────────────
   * ‣ Handlers
   * ──────────────────*/
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    /* 1. update form object */
    if (name === 'radio_buttons_group') {
      setValue(value);
    }
    setConsult((prev) => ({ ...prev, [name]: value }));

    /* 2. field‑level validation */
    if (name === 'email') {
      setIsValidEmail(emailRegex.test(value));
    }

  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    const res = await emailjsSend(consult, 'template_8zhh6tf');
    setIsSending(false);

    if (res) {
      setError(false);
      setSuccess(true);
    } else {
      setError(true);
    }
  };

  /**──────────────────
   * ‣ Disable/enable button
   * ──────────────────*/
  useEffect(() => {
    const allFilled =
      consult.full_name &&
      consult.business_name &&
      consult.phone &&
      consult.email &&
      consult.Address &&
      consult.postal_code &&
      consult.date &&
      consult.time &&
      consult.radio_buttons_group &&
      consult.message;
    console.log(consult);

    if (allFilled && isValidEmail && isValidPostal) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [consult, isValidEmail, isValidPostal]);

  /**──────────────────
   * ‣ Render
   * ──────────────────*/
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div className={s.insideModal}>
        {/* ───── Left column ───── */}
        <div className={s.leftSide}>
          <button className={s.whiteButton} onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              height="27"
              viewBox="0 0 26 27"
              fill="none"
            >
              <path
                d="M19.2004 7.1001L6.40039 19.9001M6.40039 7.1001L19.2004 19.9001"
                stroke="white"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Close
          </button>

          <h1>Let’s Schedule Your Consultation</h1>
          <p>
            Ready to illuminate your space? Fill out the form to book your
            consultation and get started on brightening up your place.
          </p>
        </div>

        {/* ───── Right column (states) ───── */}
        {success ? (
          <div className={s.thanks}>
            <img src={Thankssvg} alt="Thank you" />
            <h3>Thank you for submitting</h3>
            <p>Your message has been sent!</p>
          </div>
        ) : isSending ? (
          <div className={s.Submitting}>
            <CircularProgress style={{ width: 50, height: 50 }} />
            Submitting…
          </div>
        ) : (
          <div className={s.rightSide}>
            {/* ── Full name + business ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Full Name</p>
                <input
                  placeholder="Enter your full name"
                  type="text"
                  name="full_name"
                  id="full_name"
                  onChange={handleFormChange}
                />
              </div>

              <div className={s.input}>
                <p>Business Name</p>
                <input
                  placeholder="Enter your business name"
                  type="text"
                  name="business_name"
                  id="business_name"
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Email + phone ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>E‑mail Address</p>
                <input
                  placeholder="Enter your e‑mail address"
                  type="email"
                  name="email"
                  id="email"
                  onChange={handleFormChange}
                />
              </div>

              <div className={s.input}>
                <p>Phone Number</p>
                <input
                  placeholder="Enter your phone number"
                  type="text"
                  name="phone"
                  id="phone"
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Address + postal code ── */}
            <div className={`${s.rowInputs} ${s.addrZip}`}>
              <div className={`${s.input} ${s.addressInput}`}>
                <p>Address</p>
                <input
                  placeholder="123 Main St, Toronto, ON"
                  type="text"
                  name="Address"
                  id="Address"
                  onChange={handleFormChange}
                />
              </div>

              <div className={`${s.input} ${s.zipInput}`}>
                <p>Postal Code</p>
                <input
                  placeholder="A1A 1A1"
                  type="text"
                  name="postal_code"
                  id="postal_code"
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Service type radio group ── */}
            <div className={s.radioGroup}>
              <p>Service Type</p>
              <RadioGroup
                className={s.groupWrapper}
                name="radio_buttons_group"
                value={value}
                onChange={handleFormChange}
              >
                {['Residential', 'Commercial', 'Industrial'].map((opt) => (
                  <FormControlLabel
                    key={opt}
                    className={value === opt ? s.radio1 : s.radio2}
                    value={opt}
                    control={<Radio />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
            </div>

            {/* ── Date + time ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Preferred Dates &amp; Times</p>
                <div className={s.selectWrapper}>
                  <input
                    onFocus={(e) => (e.target.type = 'date')}
                    type="text"
                    placeholder="Select Date"
                    name="date"
                    id="date"
                    onChange={handleFormChange}
                  />
                  <input
                    onFocus={(e) => (e.target.type = 'time')}
                    type="text"
                    placeholder="Select Time"
                    name="time"
                    id="time"
                    onChange={handleFormChange}
                  />
                </div>
              </div>
            </div>

            {/* ── Message ── */}
            <div className={s.rowInputs}>
              <div className={s.input}>
                <p>Message</p>
                <textarea
                  rows={5}
                  placeholder="Please provide details about the service request"
                  name="message"
                  id="message"
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {/* ── Submit ── */}
            <button
              className={`${s.redButton} ${disabled && 'disabled'}`}
              type="submit"
              disabled={disabled || isSending}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConsultationModal;
